import {
  IconAi,
  IconArmchair,
  IconAspectRatio,
  IconBadge4k,
  IconBadgeTm,
  IconClock,
  IconHome,
  IconId,
  IconLetterL,
  IconLetterM,
  IconLetterS,
  IconPalette,
  IconPencil,
  IconSofa,
  IconUser,
  type TablerIcon,
} from "@tabler/icons-react";
import { getOneOffTierConfig } from "@repo/api/utils/billing";
import { DFY_PACKAGE_IMAGE_COUNTS } from "@repo/api/utils/dfy-billing";
import { formatPrice } from "@/lib/utils";
import { DfyAddOns, DfyOrderType, DfyTemplateType } from "@repo/api/dfy";

export interface DfyMediaPreview {
  video: string;
}

const PREVIEW_BASE_URL =
  "https://amplifiles-assets-manual.s3.eu-central-1.amazonaws.com/dfy-preview";

export interface DfyOptionContent {
  name: string;
  description: string;
  icon: TablerIcon;
  iconClassName?: string;
  preview?: DfyMediaPreview;
}

export interface DfyPackageContent extends DfyOptionContent {
  id: DfyOrderType;
}
export const DFY_PACKAGE_CONTENT: DfyPackageContent[] = [
  {
    id: "SMALL",
    name: "Small package",
    description: `Up to ${DFY_PACKAGE_IMAGE_COUNTS.SMALL} images · about 30 sec video`,
    icon: IconLetterS,
  },
  {
    id: "MEDIUM",
    name: "Medium package",
    description: `Up to ${DFY_PACKAGE_IMAGE_COUNTS.MEDIUM} images · about 40 sec video`,
    icon: IconLetterM,
  },
  {
    id: "LARGE",
    name: "Large package",
    description: `Up to ${DFY_PACKAGE_IMAGE_COUNTS.LARGE} images · about 50 sec video`,
    icon: IconLetterL,
  },
];

export const DFY_PACKAGE_FEATURES: { label: string; icon: TablerIcon }[] = [
  { label: "Delivered in 24 h", icon: IconClock },
  { label: "Unlimited free edits", icon: IconPencil },
  {
    label: "Videos in 3 aspect ratios (Horizontal, vertical and square)",
    icon: IconAspectRatio,
  },
];

export interface DfyPackageExample {
  vimeoId: string;
  thumbnail: string;
  type: DfyOrderType;
  addOn?: keyof DfyAddOns;
}

export const DFY_PACKAGE_EXAMPLES: DfyPackageExample[] = [
  {
    vimeoId: "1242315550",
    thumbnail:
      "https://i.vimeocdn.com/video/2220532039-649c55464a6587252090a0e3447a9038a45fb01138a41b5c45c8687cdc6ba1be-d_640x360",
    type: "LARGE",
    addOn: "branding",
  },
  {
    vimeoId: "1242315549",
    thumbnail:
      "https://i.vimeocdn.com/video/2220541103-dc4ad1cfb4f261ea167d2e7fc6c077c531c320b039550feea82b7d733e491007-d_640x360",
    type: "MEDIUM",
    addOn: "lifestyle",
  },
  {
    vimeoId: "1242314852",
    thumbnail:
      "https://i.vimeocdn.com/video/2220531316-7b568eca4ae8b0d64fa44b5a4faf6f6eb679b439d27a9e9e1e871b5e475d9edb-d_640x360",
    type: "SMALL",
  },
  {
    vimeoId: "1242314854",
    thumbnail:
      "https://i.vimeocdn.com/video/2220531276-9adbd70b93cd2f975d06cd162f2fd57ca9b306a5d9075146c329a12ef9fcda88-d_640x360",
    type: "SMALL",
    addOn: "aiLabel",
  },
  {
    vimeoId: "1242314855",
    thumbnail:
      "https://i.vimeocdn.com/video/2220531238-a7a396a6bdc071823c588dd3a64d6b08469e81b67dcf2635ca0aa08bc91064b1-d_640x360",
    type: "SMALL",
  },
  {
    vimeoId: "1242314853",
    thumbnail:
      "https://i.vimeocdn.com/video/2220531315-05a326a1571e86f9e5f68b64b999b76705e5b641f4e06929215940f3c74c86ae-d_640x360",
    type: "SMALL",
  },
];

export interface DfyAddOnContent extends DfyOptionContent {
  id: keyof DfyAddOns;
}

export const DFY_ADD_ON_CONTENT: DfyAddOnContent[] = [
  {
    id: "branding",
    name: "Branding",
    description: "Add property and agent details to the video.",
    icon: IconPalette,
    preview: {
      video: `${PREVIEW_BASE_URL}/Branding.mp4`,
    },
  },
  {
    id: "lifestyle",
    name: "Lifestyle",
    description: "Add people and pets to bring your rooms to life (3 images).",
    icon: IconUser,
    preview: {
      video: `${PREVIEW_BASE_URL}/Lifestyle.mp4`,
    },
  },
  {
    id: "virtualStaging",
    name: "Virtual staging",
    description: "Add furniture to your empty rooms (3 images).",
    icon: IconSofa,
    preview: {
      video: `${PREVIEW_BASE_URL}/Virtual-staging.mp4`,
    },
  },
  {
    id: "export4k",
    name: "4K export",
    description: "Deliver every video in crisp ultra-high 4K resolution.",
    icon: IconBadge4k,
    preview: {
      video: `${PREVIEW_BASE_URL}/4K.mp4`,
    },
  },
  {
    id: "furnitureReveal",
    name: "Furniture reveal",
    description: "Furniture drop-in effect for your main room (1 image).",
    icon: IconArmchair,
    preview: {
      video: `${PREVIEW_BASE_URL}/Furniture-reveal.mp4`,
    },
  },
  {
    id: "aiLabel",
    name: "AI label",
    description: "Show a label indicating the video was created with AI.",
    icon: IconAi,
    iconClassName: "size-6 sm:size-7",
    preview: {
      video: `${PREVIEW_BASE_URL}/AI-Label.mp4`,
    },
  },
];

export interface DfyTemplateContent extends DfyOptionContent {
  id: DfyTemplateType;
}

export const DFY_TEMPLATE_CONTENT: DfyTemplateContent[] = [
  {
    id: "PROPERTY_INTRO",
    name: "Property details",
    description: "Show the listing details at the beginning of the video.",
    icon: IconHome,
    preview: {
      video: `${PREVIEW_BASE_URL}/Property-details.mp4`,
    },
  },
  {
    id: "AGENT_OUTRO",
    name: "Agent details",
    description: "Show your contact information at the end of the video.",
    icon: IconId,
    preview: {
      video: `${PREVIEW_BASE_URL}/Agent-details.mp4`,
    },
  },
  {
    id: "LOGO",
    name: "Logo",
    description: "Show your logo in the video.",
    icon: IconBadgeTm,
  },
];

export function formatDfyCreditsToPrice(credits: number): string {
  const { price, currency } = getOneOffTierConfig(credits);
  return formatPrice(price, currency) ?? "";
}
